// src/pages/AddUser.jsx

import React, { useState } from "react";
import { db, auth, storage } from "../firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from "firebase/auth";
import { useAuth } from "../context/AuthContext";
import "./AddUser.css";

const AddUser = ({ onUserAdded }) => {
  const { userData: adminData } = useAuth();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("employee");
  const [department, setDepartment] = useState("");
  const [position, setPosition] = useState("");
  const [joiningDate, setJoiningDate] = useState("");
  const [phone, setPhone] = useState("");
  const [employeeId, setEmployeeId] = useState("");  const [reportingManager, setReportingManager] = useState("Hanumat Lal Vishwakarma (Founder & Managing Director)");  const [adminPassword, setAdminPassword] = useState("");
  const [tempPassword, setTempPassword] = useState("Password123");
  const [loading, setLoading] = useState(false);

  // --- ✨ NEW BANK/STATUTORY STATES ---
  const [location, setLocation] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");  const [ifscCode, setIfscCode] = useState("");  const [panNumber, setPanNumber] = useState("");
  const [pfNumber, setPfNumber] = useState("");
  const [uanNumber, setUanNumber] = useState("");
  const [grossMonthlySalary, setGrossMonthlySalary] = useState("");
  const [annualCTC, setAnnualCTC] = useState("");
  
  // Profile picture states
  const [profilePic, setProfilePic] = useState(null);
  const [profilePicPreview, setProfilePicPreview] = useState(null);
  const [uploadingPic, setUploadingPic] = useState(false);

  // Handle profile picture selection
  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('File size must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      setProfilePic(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle profile picture upload to Firebase Storage
  const uploadProfilePicture = async (userId) => {
    if (!profilePic) return "";
    
    setUploadingPic(true);
    try {
      const storageRef = ref(storage, `profile_pictures/${userId}`);
      const uploadTask = uploadBytesResumable(storageRef, profilePic);
      
      return new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          null, // No progress tracking needed here
          (error) => reject(error),
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          }
        );
      });
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      throw error;
    } finally {
      setUploadingPic(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !tempPassword || !adminPassword || !employeeId) {
      return alert("Name, Email, New User Password, Your Password, and Employee ID are required");
    }
    
    // Prevent double submission
    if (loading) return;
    
    setLoading(true);

    const adminEmail = auth.currentUser.email;
    let newUserUid = null;

    try {
      // Temporarily create the new user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, tempPassword);
      newUserUid = userCredential.user.uid;

      // Re-authenticate as the admin to get permissions back
      await signInWithEmailAndPassword(auth, adminEmail, adminPassword);

      // Upload profile picture if selected
      let photoURL = "";
      if (profilePic) {
        try {
          photoURL = await uploadProfilePicture(newUserUid);
        } catch (picError) {
          console.error('Profile picture upload failed:', picError);
          // Continue without profile picture
        }
      }

      // 1. Create the main user document
      const userData = {
        name,
        email,
        role,
        department: department || "",
        position: position || "",
        photoURL: photoURL,
        joiningDate: joiningDate || new Date().toISOString().substring(0, 10),
        phone: phone || "",
        employeeId: employeeId,
        uid: newUserUid,
        // --- ✨ ADDED NEW FIELDS ---
        location: location || "",
        bankName: bankName || "",
        bankAccountNumber: bankAccountNumber || "",
        panNumber: panNumber || "",
        pfNumber: pfNumber || "",
        uanNumber: uanNumber || "",
        grossMonthlySalary: grossMonthlySalary || "",
        annualCTC: annualCTC || "",
        performance_kpi: 0,  // Default: 0 at joining; updated by admin/superadmin
        reportingManager: reportingManager || "Hanumat Lal Vishwakarma (Founder & Managing Director)",
      };
      await setDoc(doc(db, "users", newUserUid), userData);

      // 2. ✨ AUTO-SYNC: Create comprehensive employee record for Employee List
      const employeeRecordData = {
        employeeId: employeeId,
        name: name,
        email: email,
        phone: phone || "",
        department: department || "",
        position: position || "",
        role: role,
        photoURL: photoURL,
        joiningDate: userData.joiningDate,
        leavingDate: null,
        status: "active", // active or resigned
        uid: newUserUid,
        
        // Bank & Statutory Info
        location: location || "",
        bankName: bankName || "",
        bankAccountNumber: bankAccountNumber || "",
        ifscCode: ifscCode || "",
        panNumber: panNumber || "",
        pfNumber: pfNumber || "",
        uanNumber: uanNumber || "",
        grossMonthlySalary: grossMonthlySalary || "",
        annualCTC: annualCTC || "",
        performance_kpi: 0,  // Default: 0 at joining; updated by admin/superadmin
        reportingManager: reportingManager || "Hanumat Lal Vishwakarma (Founder & Managing Director)",

        // Salary History (empty initially, populated when salary slips created)
        salaryHistory: {},
        lastSalaryUpdateMonth: null,
        lastSalaryUpdateDate: null,
        
        // Documents (populated via Employee Detail Page) - Object structure for document types
        documents: {},
        
        // Drive links (populated via Employee Detail Page) - Object structure for link types
        driveLinks: {},
        
        // Internal notes (populated via Employee Detail Page) - Array for notes list
        notes: [],
        
        // Role History (initial entry at joining)
        roleLog: [{
          date: userData.joiningDate,
          oldRole: null,
          newRole: role,
          oldPosition: null,
          newPosition: position || "",
          note: "Initial role at joining 🎉",
          promotedBy: adminData.name || adminData.email || "System",
          promotedAt: new Date().toISOString(),
        }],

        // Experience Letter flags (updated when employee resigns)
        hasExperienceLetter: false,
        experienceRecordId: null,
        
        // Metadata
        createdAt: serverTimestamp(),
        createdBy: adminData.uid,
        updatedAt: serverTimestamp(),
        syncedAt: serverTimestamp()
      };
      await setDoc(doc(db, "employee_records_complete", employeeId), employeeRecordData);

      alert(`✅ User added successfully!\n✅ Auto-synced to Employee List\n(Temporary Password: ${tempPassword})`);
      
      // Reset loading before callback
      setLoading(false);
      
      // Call parent callback
      if (onUserAdded) onUserAdded();
      
      // Reset form fields
      setName("");
      setEmail("");
      setRole("employee");
      setDepartment("");
      setPosition("");
      setAdminPassword("");
      setJoiningDate("");
      setPhone("");
      setEmployeeId("");
      setReportingManager("Hanumat Lal Vishwakarma (Founder & Managing Director)");
      // --- ✨ RESET NEW STATES ---
      setLocation("");
      setBankName("");
      setBankAccountNumber("");
      setIfscCode("");
      setPanNumber("");
      setPfNumber("");
      setUanNumber("");
      setGrossMonthlySalary("");
      setAnnualCTC("");
      setProfilePic(null);
      setProfilePicPreview(null);

    } catch (err) {
      console.error("Add User Error:", err);
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        alert("Action failed: The password you entered for your own admin account is incorrect. Please try again.");
      } else {
        alert("Failed to create user: " + err.message);
      }
    } finally {
      // Always reset loading state
      setLoading(false);    }
  };
  return (
    <div className="add-user-container">
      <h2>➕ Add New User</h2>
      <form onSubmit={handleAddUser}>
        
        {/* === SECTION 1: Basic Information === */}
        <h4>📋 Basic Information</h4>
        
        {/* Profile Picture Upload */}
        <div className="add-user-form-grid single-column">
          <div className="add-user-field">
            <label htmlFor="profilePic">Profile Picture</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              {profilePicPreview && (
                <img 
                  src={profilePicPreview} 
                  alt="Profile Preview" 
                  style={{ 
                    width: '100px', 
                    height: '100px', 
                    borderRadius: '50%', 
                    objectFit: 'cover',
                    border: '3px solid #2196f3'
                  }} 
                />
              )}
              <div style={{ flex: 1 }}>
                <input 
                  id="profilePic" 
                  type="file" 
                  accept="image/*"
                  onChange={handleProfilePicChange}
                  style={{ display: 'none' }}
                />
                <label 
                  htmlFor="profilePic" 
                  style={{ 
                    display: 'inline-block',
                    padding: '10px 20px',
                    background: '#2196f3',
                    color: 'white',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {profilePic ? '✓ Change Picture' : '📸 Upload Picture'}
                </label>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  Max 5MB • JPG, PNG, GIF
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="add-user-form-grid single-column">
          <div className="add-user-field">
            <label htmlFor="name">Full Name <span className="required-mark">*</span></label>
            <input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Enter full name" />
          </div>
        </div>

        <div className="add-user-form-grid">
          <div className="add-user-field">
            <label htmlFor="email">Email Address <span className="required-mark">*</span></label>
            <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="email@example.com" />
          </div>
          <div className="add-user-field">
            <label htmlFor="employeeId">Employee ID <span className="required-mark">*</span></label>
            <input id="employeeId" type="text" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} required placeholder="e.g., EMP001" />
          </div>
        </div>

        {/* === SECTION 2: Authentication === */}
        <h4>🔐 Authentication Details</h4>
        <div className="add-user-form-grid">
          <div className="add-user-field">
            <label htmlFor="new-password">New User Password <span className="required-mark">*</span></label>
            <input id="new-password" type="text" value={tempPassword} onChange={(e) => setTempPassword(e.target.value)} required placeholder="Temporary password" />
          </div>
          <div className="add-user-field">
            <label htmlFor="admin-password">Your Admin Password <span className="required-mark">*</span></label>
            <input id="admin-password" type="password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} required placeholder="Verify your identity" />
          </div>
        </div>

        {/* === SECTION 3: Job Details === */}
        <h4>💼 Job Information</h4>
        <div className="add-user-form-grid">
          <div className="add-user-field">
            <label htmlFor="role">Role</label>
            <select id="role" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="employee">Employee</option>
              {adminData?.role === "superadmin" && <option value="admin">Admin</option>}
            </select>
          </div>
          <div className="add-user-field">
            <label htmlFor="joiningDate">Joining Date</label>
            <input id="joiningDate" type="date" value={joiningDate} onChange={(e) => setJoiningDate(e.target.value)} />
          </div>
        </div>

        <div className="add-user-form-grid">
          <div className="add-user-field">
            <label htmlFor="department">Department</label>
            <input id="department" type="text" value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="e.g., Technology" />
          </div>
          <div className="add-user-field">
            <label htmlFor="position">Position</label>
            <input id="position" type="text" value={position} onChange={(e) => setPosition(e.target.value)} placeholder="e.g., Content Creator" />
          </div>
        </div>

        <div className="add-user-form-grid single-column">
          <div className="add-user-field">
            <label htmlFor="reportingManager">Reporting Manager</label>
            <input id="reportingManager" type="text" value={reportingManager} onChange={(e) => setReportingManager(e.target.value)} placeholder="Hanumat Lal Vishwakarma (Founder & Managing Director)" />
            <small style={{ color: '#888', fontSize: '0.78rem' }}>Default: Hanumat Lal Vishwakarma (Founder &amp; Managing Director)</small>
          </div>
        </div>

        {/* === SECTION 4: Contact Details === */}
        <h4>📞 Contact Information</h4>
        <div className="add-user-form-grid">
          <div className="add-user-field">
            <label htmlFor="phone">Phone Number</label>
            <input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 XXXXXXXXXX" />
          </div>
          <div className="add-user-field">
            <label htmlFor="location">Location</label>
            <input id="location" type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City, State" />
          </div>
        </div>
        
        {/* === SECTION 5: Bank & Statutory Details === */}
        <h4>🏦 Bank & Statutory Details</h4>
        <div className="add-user-form-grid">
          <div className="add-user-field">
            <label htmlFor="bankName">Bank Name</label>
            <input id="bankName" type="text" value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g., HDFC Bank" />
          </div>
          <div className="add-user-field">
            <label htmlFor="bankAccountNumber">Bank Account Number</label>
            <input id="bankAccountNumber" type="text" value={bankAccountNumber} onChange={(e) => setBankAccountNumber(e.target.value)} placeholder="Account number" />
          </div>
        </div>

        <div className="add-user-form-grid">
          <div className="add-user-field">
            <label htmlFor="ifscCode">IFSC Code</label>
            <input id="ifscCode" type="text" value={ifscCode} onChange={(e) => setIfscCode(e.target.value)} placeholder="e.g., HDFC0001234" />
          </div>
          <div className="add-user-field">
            {/* Empty field for grid alignment */}
          </div>
        </div>

        <div className="add-user-form-grid">
          <div className="add-user-field">
            <label htmlFor="panNumber">PAN Number</label>
            <input id="panNumber" type="text" value={panNumber} onChange={(e) => setPanNumber(e.target.value)} placeholder="ABCDE1234F" />
          </div>
          <div className="add-user-field">
            <label htmlFor="pfNumber">PF Number</label>
            <input id="pfNumber" type="text" value={pfNumber} onChange={(e) => setPfNumber(e.target.value)} placeholder="PF account number" />
          </div>
        </div>

        <div className="add-user-form-grid">
          <div className="add-user-field">
            <label htmlFor="uanNumber">UAN (Universal Account Number)</label>
            <input id="uanNumber" type="text" value={uanNumber} onChange={(e) => setUanNumber(e.target.value)} placeholder="UAN number" />
          </div>
          <div className="add-user-field">
            <label htmlFor="grossMonthlySalary">Monthly Gross Salary (₹)</label>
            <input id="grossMonthlySalary" type="number" value={grossMonthlySalary} onChange={(e) => setGrossMonthlySalary(e.target.value)} placeholder="e.g. 25000" />
          </div>
        </div>

        <div className="add-user-form-grid">
          <div className="add-user-field">
            <label htmlFor="annualCTC">Total CTC (₹)</label>
            <input id="annualCTC" type="number" value={annualCTC} onChange={(e) => setAnnualCTC(e.target.value)} placeholder="e.g. 300000" />
          </div>
          <div className="add-user-field" />
        </div>
        <p style={{ gridColumn: "1 / -1", fontSize: "0.8rem", color: "#6b7280", margin: "-8px 0 12px" }}>
          These are informational only. Set the employee's fixed salary breakdown and variable components via
          Employee Detail → Appraise after creating the user, before running payroll auto-fill.
        </p>

        <button type="submit" disabled={loading || uploadingPic} className="primary-btn" data-tooltip={loading ? "Adding User..." : uploadingPic ? "Uploading Picture..." : "Add New User"}>
          {loading ? "⏳ Adding User..." : uploadingPic ? "📤 Uploading Picture..." : "✅ Add User"}
        </button>
      </form>
    </div>
  );
};

export default AddUser;